import React from 'react';
import { Link } from 'react-router-dom';
import { PostCard } from '@/types/blog';
import { ChevronRightIcon } from '@heroicons/react/24/outline';

interface TopicSidebarProps {
  posts: PostCard[];
  currentArticleUuid?: string;
  topicSlug: string;
  loading?: boolean;
  className?: string;
}

export const TopicSidebar: React.FC<TopicSidebarProps> = ({
  posts,
  currentArticleUuid,
  topicSlug,
  loading = false,
  className = ''
}) => {
  if (loading) {
    return (
      <aside className={`bg-m2-surface-lowest rounded-eh border border-eh-border shadow-sm p-6 ${className}`}>
        <div className="space-y-4">
          <div className="h-6 bg-m2-surface-highest rounded animate-pulse"></div>
          <div className="space-y-3">
            {Array.from({ length: 5 }).map((_, index) => (
              <div key={index} className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-m2-surface-highest rounded animate-pulse flex-shrink-0"></div>
                <div className="flex-1">
                  <div className="h-4 bg-m2-surface-highest rounded animate-pulse mb-2"></div>
                  <div className="h-3 bg-m2-surface-highest rounded animate-pulse w-3/4"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </aside>
    );
  }

  if (posts.length === 0) {
    return (
      <aside className={`bg-m2-surface-lowest rounded-eh border border-eh-border shadow-sm p-6 ${className}`}>
        <h3 className="text-lg font-semibold text-m2-on-surface mb-4">
          Bài viết cùng chủ đề
        </h3>
        <div className="text-center py-6">
          <p className="text-m2-on-surface-variant">No articles found in this topic.</p>
        </div>
      </aside>
    );
  }

  return (
    <aside className={`bg-m2-surface-lowest rounded-eh border border-eh-border shadow-sm p-6 ${className}`}>
      <h3 className="text-lg font-semibold text-m2-on-surface mb-6">
        Bài viết cùng chủ đề ({posts.length})
      </h3>
      
      <nav className="space-y-3">
        {posts.map((post) => {
          const isCurrentArticle = post.uuid === currentArticleUuid;
          const articleUrl = `/blog/articles/${post.slug || post.uuid}`;
          
          return (
            <Link
              key={post.uuid}
              to={articleUrl}
              className={`
                group flex items-start space-x-3 p-3 rounded-eh border transition-all duration-200 hover:shadow-md
                ${isCurrentArticle 
                  ? 'bg-eh-sage border-eh-forest-ink/30 ring-2 ring-eh-forest-ink/30' 
                  : 'border-m2-outline-variant hover:border-m2-outline hover:bg-m2-surface-low'
                }
              `}
            >
              {/* Thumbnail */}
              <div className="flex-shrink-0 w-12 h-12 rounded-md overflow-hidden bg-m2-surface-highest">
                {post.avatarUrl ? (
                  <img 
                    src={post.avatarUrl} 
                    alt={post.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-eh-sage">
                    <span className="text-eh-forest-ink text-xs font-medium">
                      {post.title.charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
              </div>
              
              {/* Content */}
              <div className="flex-1 min-w-0">
                <h4 className={`
                  font-medium text-sm leading-5 mb-1 group-hover:text-eh-forest-ink transition-colors duration-200
                  ${isCurrentArticle ? 'text-eh-forest-ink' : 'text-m2-on-surface'}
                `}>
                  {post.title}
                </h4>
                
                <div className="flex items-center justify-between text-xs text-m2-on-surface-variant">
                  <span className="flex items-center space-x-1">
                    <span>{post.totalView} views</span>
                  </span>
                  
                  <ChevronRightIcon 
                    className={`
                      w-4 h-4 transition-transform duration-200 group-hover:translate-x-1
                      ${isCurrentArticle ? 'text-eh-forest-ink' : 'text-m2-outline'}
                    `} 
                  />
                </div>
              </div>
            </Link>
          );
        })}
      </nav>

      {/* Show more articles link if there are many */}
      {posts.length >= 10 && (
        <div className="mt-6 pt-4 border-t border-m2-outline-variant">
          <Link
            to={`/blog/topics/${topicSlug}`}
            className="text-sm text-eh-forest-ink hover:underline font-medium inline-flex items-center"
          >
            View all articles in this topic
            <ChevronRightIcon className="w-4 h-4 ml-1" />
          </Link>
        </div>
      )}
    </aside>
  );
};

export default TopicSidebar;